import { STATION_TIME_ZONE } from '../../../utils/stationTime';
// The date range the utilization view asks the occupancy endpoint for.
//
// Pure and separate from the component because it is the piece with a real rule
// in it: the backend measures the requested span in milliseconds
// (`(to - from) / 86_400_000` in the new-backend's group controller) and rejects
// anything longer than 31 days, while the tab labels are calendar days. Across a
// DST transition those two are not the same thing.

/** Mirrors `MAX_OCCUPANCY_DAYS` in new-backend/admin/src/controllers/groupController.ts. */
export const MAX_OCCUPANCY_RANGE_DAYS = 31;

const MS_PER_DAY = 86_400_000;

export interface UtilizationRange {
    /** ISO date-time, inclusive. */
    from: string;
    /** ISO date-time, exclusive. */
    to: string;
}

/**
 * The end of the range: the calendar day boundary, capped at `rangeDays × 24 h`.
 *
 * Both directions of the DST shift matter, which is why this is a clamp rather
 * than either arithmetic on its own:
 *
 *  - **October fall-back.** `rangeDays` calendar days from local midnight last
 *    `rangeDays` days *and one hour* — 31.04 by the backend's measure, so the
 *    31-day tab answered 400 for every day whose range still contained that
 *    Sunday. The cap keeps the requested span at what the tab advertises.
 *  - **March spring-forward.** The same arithmetic gives one hour *less* than
 *    `rangeDays × 24 h`, so the calendar boundary is the earlier value and is
 *    kept. Using the millisecond span alone would push the end an hour past
 *    midnight and add a stray, near-empty final day row to the grid.
 *
 * Zone-independent: it reads the two candidate instants it is given, so a
 * browser in a zone without DST simply gets two identical candidates.
 */
export const clampRangeEnd = (start: Date, dayBoundary: Date, rangeDays: number): Date =>
    new Date(Math.min(dayBoundary.getTime(), start.getTime() + rangeDays * MS_PER_DAY));

/** The DEFAULT zone the grid is anchored in — the station's (`STATION_TIME_ZONE`); the
 * backend groups its cells the same way. Since #3219 a grid reads the zone of
 * its group's location (the occupancy response's `timeZone`) and passes it on. */
export const GRID_TZ = STATION_TIME_ZONE;

/**
 * How far `timeZone` is ahead of UTC at this instant, in milliseconds.
 *
 * Read off `Intl` rather than hard-coded, so daylight saving is handled
 * instead of approximated (ADR-0019 makes the same point for tariff bounds:
 * an IANA zone, never a fixed +1/+2).
 */
const zoneOffsetMs = (instant: Date, timeZone: string): number => {
    const parts: Record<string, string> = {};
    new Intl.DateTimeFormat('en-CA', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(instant).forEach(({ type, value }) => {
        parts[type] = value;
    });

    const wallClockAsUtc = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        // `hour12: false` reports midnight as 24 in some engines.
        Number(parts.hour) % 24,
        Number(parts.minute),
        Number(parts.second),
    );

    return wallClockAsUtc - instant.getTime();
};

/**
 * Midnight at the start of `instant`'s calendar day in `timeZone`, plus
 * `addDays` whole calendar days.
 *
 * Resolved twice on purpose. The offset at `instant` and the offset at the
 * midnight being computed differ on a changeover day, so the first pass finds
 * the right day and the second pass anchors it with that day's own offset.
 * Calendar arithmetic runs in UTC, where a day is always 24 hours and no
 * changeover can swallow or duplicate one.
 */
export const zonedDayStart = (
    instant: Date,
    timeZone: string,
    addDays = 0,
): Date => {
    const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant).split('-').map(Number);

    const utcMidnight = Date.UTC(year as number, (month as number) - 1, (day as number) + addDays);
    const firstPass = new Date(utcMidnight - zoneOffsetMs(instant, timeZone));

    return new Date(utcMidnight - zoneOffsetMs(firstPass, timeZone));
};

/**
 * `[from, to)` for a range tab of `rangeDays` calendar days, starting at the
 * beginning of `now`'s day — the operator thinks in days, not in "now".
 *
 * Anchored in the **location's** zone (`timeZone`, default the station's), not
 * the browser's. The backend groups cells into days in that same zone, so a
 * viewer in another zone used to get a
 * range offset against that grouping — and the grid then drew a stray final
 * row, empty across every hour, carrying a non-zero peak. The same class of
 * self-contradicting row the drawn-hours fix removed, one layer up. Latent
 * while every tenant is German, which is exactly why it would have survived.
 */
export const utilizationRange = (rangeDays: number, now: Date, timeZone: string = GRID_TZ): UtilizationRange => {
    const start = zonedDayStart(now, timeZone);
    const dayBoundary = zonedDayStart(now, timeZone, rangeDays);

    return {
        from: start.toISOString(),
        to: clampRangeEnd(start, dayBoundary, rangeDays).toISOString(),
    };
};
